import React, { createContext, useContext, useState, useEffect, useCallback } from 'react'
import { Language } from '../types/cms.types'
import {
    HeroContent,
    IntroContent,
    MenuContent,
    MenuDetailContent,
    GalleryContent,
    ReviewsContent,
    FooterContent,
    VotedKeywordsContent,
    PressContent
} from '../types/content.types'
import { cmsService } from '../services/CMSService'
import { useLanguage } from './LanguageContext'

export interface CMSContentState {
    hero: HeroContent | null
    intro: IntroContent | null
    menu: MenuContent | null
    menuDetail: MenuDetailContent | null
    gallery: GalleryContent | null
    reviews: ReviewsContent | null
    footer: FooterContent | null
    votedKeywords: VotedKeywordsContent | null
    press: PressContent | null
}

const emptyContent: CMSContentState = {
    hero: null, intro: null, menu: null,
    menuDetail: null, gallery: null, reviews: null, footer: null,
    votedKeywords: null, press: null,
}

const getSSRContent = (): CMSContentState | null => {
    if (typeof window === 'undefined') return null
    return (window as unknown as { __CMS_INITIAL__?: CMSContentState }).__CMS_INITIAL__ ?? null
}

interface CMSContextType {
    content: CMSContentState
    loading: boolean
    error: string | null
    loadContent: () => Promise<void>
    refreshContent: () => Promise<void>
}

const CMSContext = createContext<CMSContextType | undefined>(undefined)

export const CMSProvider: React.FC<{ children: React.ReactNode; initialContent?: CMSContentState }> = ({ children, initialContent }) => {
    const { language } = useLanguage()
    const ssrContent = initialContent ?? getSSRContent()
    const skipFirstSpinner = React.useRef(Boolean(ssrContent))

    const [content, setContent] = useState<CMSContentState>(ssrContent ?? emptyContent)
    const [loading, setLoading] = useState(!ssrContent)
    const [error, setError] = useState<string | null>(null)

    const loadContent = useCallback(async () => {
        if (!skipFirstSpinner.current) setLoading(true)
        skipFirstSpinner.current = false
        setError(null)

        try {
            const [hero, intro, menu, menuDetail, gallery, reviews, footer, votedKeywords, press] = await Promise.all([
                cmsService.loadHeroContent(language),
                cmsService.loadIntroContent(language),
                cmsService.loadMenuContent(language),
                cmsService.loadMenuDetailContent(language),
                cmsService.loadGalleryContent(language),
                cmsService.loadReviewsContent(language),
                cmsService.loadFooterContent(language),
                cmsService.loadVotedKeywordsContent(language),
                cmsService.loadPressContent(language)
            ])

            setContent({ hero, intro, menu, menuDetail, gallery, reviews, footer, votedKeywords, press })
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Failed to load content'
            setError(errorMessage)
            console.error('CMS Content loading error:', err)
        } finally {
            setLoading(false)
        }
    }, [language])

    const refreshContent = useCallback(async () => {
        cmsService.clearCacheForLanguage(language)
        await loadContent()
    }, [language, loadContent])

    // Load content when language changes
    useEffect(() => {
        loadContent()
    }, [loadContent])

    const value: CMSContextType = {
        content,
        loading,
        error,
        loadContent,
        refreshContent
    }

    return (
        <CMSContext.Provider value={value}>
            {children}
        </CMSContext.Provider>
    )
}

export const useCMS = (): CMSContextType => {
    const context = useContext(CMSContext)
    if (context === undefined) {
        throw new Error('useCMS must be used within a CMSProvider')
    }
    return context
}

export default CMSContext
